/**
 * HTTP Client for API communication
 * Handles base URL, headers, and error management
 */

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

if (!BACKEND_URL) {
  console.error(
    "[API Client] VITE_BACKEND_URL is not defined in environment variables",
  );
}

/**
 * Configuration for API requests
 */
interface RequestOptions extends RequestInit {
  /** Whether to parse response as JSON (default: true) */
  parseJson?: boolean;
}

/**
 * Error thrown by API calls
 */
export interface BackendErrorResponse {
  statusCode?: number;
  message?: string;
  error?: unknown;
  timestamp?: string;
}

/**
 * General backend response wrapper
 */
interface BackendResponseWrapper<T> {
  statusCode: number;
  message: string;
  data: T;
}

type AuthTokenGetter = () => string | null;

let authTokenGetter: AuthTokenGetter = () => null;

/** Tenant slug for authenticated /tenants/me/* backoffice requests (synced from layout render). */
export const backofficeTenantScopeRef: { current: string | null } = {
  current: null,
};

/**
 * Registers a function that returns the current auth token.
 * Called by AuthProvider on mount.
 */
export const setAuthTokenGetter = (getter: AuthTokenGetter): void => {
  authTokenGetter = getter;
};

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public responseData?: BackendErrorResponse,
  ) {
    const msg = responseData?.message || statusText;
    super(msg);
    this.name = "ApiError";
  }
}

/** Dispatched when an authenticated request returns 401 */
export const AUTH_UNAUTHORIZED_EVENT = "auth:unauthorized";

/**
 * Generic HTTP client for making API requests
 * Automatically prefixes URLs with BACKEND_URL
 */
export const apiClient = {
  /**
   * Make a GET request
   * @param endpoint - API endpoint (e.g., '/castings')
   * @param options - Fetch options
   * @returns Parsed JSON response
   */
  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return apiClient.request<T>(endpoint, {
      ...options,
      method: "GET",
    });
  },

  /**
   * Make a POST request
   * @param endpoint - API endpoint
   * @param body - Request body (will be serialized to JSON)
   * @param options - Fetch options
   * @returns Parsed JSON response
   */
  async post<T>(
    endpoint: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    return apiClient.request<T>(endpoint, {
      ...options,
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  /**
   * Make a PATCH request
   * @param endpoint - API endpoint
   * @param body - Request body
   * @param options - Fetch options
   * @returns Parsed JSON response
   */
  async patch<T>(
    endpoint: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    return apiClient.request<T>(endpoint, {
      ...options,
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  /**
   * Make a DELETE request
   * @param endpoint - API endpoint
   * @param options - Fetch options
   * @returns Parsed JSON response
   */
  async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return apiClient.request<T>(endpoint, {
      ...options,
      method: "DELETE",
    });
  },

  /**
   * Core request method - handles URL construction, headers, and error handling
   * @param endpoint - API endpoint
   * @param options - Fetch options
   * @returns Parsed JSON response
   */
  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const url = `${BACKEND_URL}${endpoint}`;
    const { parseJson = true, ...fetchOptions } = options;

    // Set default headers
    const headers = new Headers(fetchOptions.headers);
    if (!headers.has("Content-Type") && fetchOptions.body) {
      headers.set("Content-Type", "application/json");
    }

    const token = authTokenGetter();
    if (token && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const backofficeTenantSlug = backofficeTenantScopeRef.current;
    if (
      backofficeTenantSlug &&
      token &&
      endpoint.includes("/tenants/me/") &&
      !headers.has("X-Tenant-Slug")
    ) {
      headers.set("X-Tenant-Slug", backofficeTenantSlug);
    }

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
      });

      // Parse response data
      const data = parseJson ? await response.json() : await response.text();

      // Handle HTTP errors
      if (!response.ok) {
        const apiError = new ApiError(
          response.status,
          response.statusText,
          data,
        );
        if (response.status === 401) {
          window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));
        }
        console.error(
          `[API Client] HTTP ${response.status} - ${response.statusText}`,
          data,
        );
        throw apiError;
      }

      // Handle response interceptor wrapper (extract actual data)
      if (
        parseJson &&
        typeof data === "object" &&
        data !== null &&
        "data" in data
      ) {
        return (data as BackendResponseWrapper<T>).data;
      }

      return data as T;
    } catch (error) {
      // Re-throw ApiError as-is
      if (error instanceof ApiError) {
        throw error;
      }

      // Handle network errors
      if (error instanceof TypeError) {
        console.error(`[API Client] Network error: ${error.message}`);
        throw new ApiError(0, "Network Error", { message: error.message });
      }

      // Re-throw unknown errors
      throw error;
    }
  },
};
